import { reactive } from "vue";
import { OPEN_ENDED_DASH } from "@components/utils/effectiveDated";
import { computePayRangeVersionState } from "@components/EmployeePayPlansTimelineAndList/employeePayPlansTimelineUtils";
import { formatFairPayDate } from "@components/utils/fairPayFormat";

const PAY_BASIS_LABEL = {
  per_year: "Year",
  per_month: "Month",
  per_hour: "Hour",
  ANNUAL: "Year",
  MONTHLY: "Month",
  HOURLY: "Hour",
};

function pick(record, snake, camel) {
  return record?.[snake] ?? record?.[camel] ?? null;
}

/** "Jan 1, 2024 – Dec 31, 2024", or `openLabel` when there is no end. */
function formatPeriod(from, to, openLabel) {
  if (!from) return "";
  if (!to) return openLabel ? `${formatFairPayDate(from)} – ${openLabel}` : formatFairPayDate(from);
  return `${formatFairPayDate(from)} – ${formatFairPayDate(to)}`;
}

/** Keep the currency's own decimals, add thousands separators. */
function formatAmount(amount) {
  if (amount == null || amount === "") return "";
  const text = String(amount);
  const number = Number(text);
  if (!Number.isFinite(number)) return text;
  const decimals = text.includes(".") ? text.split(".")[1].length : 0;
  return number.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function locationLabel(location) {
  if (!location) return "";
  return [location.country?.name, location.region?.name, location.sub_region?.name]
    .filter(Boolean)
    .join(" · ");
}

/** "P-1449.V2" -> "P-1449" */
function rangeCodeOf(versionCode) {
  const match = String(versionCode || "").match(/^(.+)\.V\d+$/i);
  return match ? match[1] : "";
}

/**
 * Map one summary pay range version (ER-12068, no extra call) and the
 * subscription it hangs off onto the presentational PayRangeVersionDrawer props.
 */
export function buildPayRangeVersionDrawerProps({ version, subscription, employee, today }) {
  const code = pick(version, "version_code", "versionCode") || "";
  const level = pick(version, "job_family_level", "jobFamilyLevel");
  const readiness = version?.readiness || {};
  const assignment = subscription?.assignment || {};
  const position = subscription?.position || {};

  return {
    state: computePayRangeVersionState(version, today) || "current",
    employee: {
      name: employee?.name || "Employee",
      title: employee?.title || "",
      avatar: employee?.avatarUrl || employee?.avatar || "",
    },
    payRangeVersion: {
      code,
      rangeCode: rangeCodeOf(code),
      effective_from: pick(version, "effective_from", "effectiveFrom"),
      effective_to: pick(version, "effective_to", "effectiveTo"),
      replacesVersionCode: pick(version, "replaces_version_code", "replacesVersionCode") || "",
      jobCategory: level?.job_family?.name || "",
      level: level?.label || level?.name || "",
      location: locationLabel(version?.location),
      workArrangement: pick(version, "work_mode_label", "workModeLabel") || "",
      currency: version?.currency || "",
      payBasis: PAY_BASIS_LABEL[pick(version, "pay_basis", "payBasis")] || "",
      amounts: version?.readiness
        ? {
            0: formatAmount(readiness.at_0),
            50: formatAmount(readiness.at_50),
            100: formatAmount(readiness.at_100),
            150: formatAmount(readiness.at_150),
          }
        : null,
    },
    sourcePosition: {
      code: position.code || "",
      name: position.name || "",
      primaryAssignmentPeriod: formatPeriod(
        pick(assignment, "effective_from", "effectiveFrom"),
        pick(assignment, "effective_to", "effectiveTo"),
        OPEN_ENDED_DASH
      ),
      subscriptionPeriod: formatPeriod(
        pick(subscription, "effective_from", "effectiveFrom"),
        pick(subscription, "effective_to", "effectiveTo"),
        ""
      ),
    },
    unreadChatter: Boolean(pick(version, "unread_chatter", "unreadChatter")),
  };
}

/**
 * State for the read-only PayRangeVersionDrawer (ER-12117). The page hands over
 * the summary version it already holds, so there is nothing to fetch.
 */
export function usePayRangeVersionDrawer() {
  const state = reactive({ open: false, versionId: null, props: null });

  function open({ version, subscription, employee, today }) {
    if (!version) return;
    state.props = buildPayRangeVersionDrawerProps({ version, subscription, employee, today });
    state.versionId = version.id;
    state.open = true;
  }

  function close() {
    state.open = false;
    state.versionId = null;
    state.props = null;
  }

  return { state, open, close };
}
